import { useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useIsNewEntry, usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  type IsAutosaveBlockedOptions,
  selectIsAutosaveBlocked,
} from "src/redux/selectors/selectIsAutosaveBlocked";
import { store } from "src/redux/store";
import {
  reassignNewChecklistActions,
  setAutoSaving,
  setCurrent,
  setEditing,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";
import {
  ESaveEntryReason,
  setLatestEntryStatus,
  setPersistInlineSave,
  setSendDataOnNewEntry,
  setShowAutoSaveModal,
  setTriggerInlineSave,
} from "src/redux/slices/generatorSlice";
import { crumb, getYachtId, isOnline } from "src/utils";

import { getCurrentUTC } from "../../utils/timeUtils/getCurrentUTC";
import { updateEntryRouteSilently } from "../utils/navigateToLogbookEntry";
import { convertLbEntryForSave } from "./useConvertLbEntry";
import { registerAutosaveScheduleHandler } from "./autosaveScheduler";
import { flushStoredChecklistQueue } from "./flushChecklistQueue";
import { useSaveContactNumber } from "./useSaveContactNumber";

/**
 * Custom hook to handle auto-saving functionality for logbook entries.
 *
 * @returns A function to manage auto-save operations for new and opened entries.
 */
export const useAutoSave = () => {
  const sections = useSelector(state => state.generatorSlice.lbentry.sections);
  const lbentry = useSelector(state => state.generatorSlice.lbentry);

  const { control, getValues } = useFormContext();
  const initialRender = useRef(true);
  const watchData = useWatch({ control });
  const isNewEntry = useIsNewEntry();
  const { post } = usePostApi();
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const { saveContactNumber } = useSaveContactNumber();
  const { _prename, entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );
  const { id } = useSelector(state => state.generatorSlice.latestEntry);
  const { persistInlineSave } = useSelector(state => state.generatorSlice);
  const { activeAutosave, editing, isSaving, current } =
    useSelector(state => state.entrySlice);
  const isAutosaveBlocked = useSelector(selectIsAutosaveBlocked);
  const isReadOnly = useSelector(
    state => state.generatorSlice.lbentry.isReadonly,
  );

  const timer = useRef<NodeJS.Timeout>();
  const scheduledOptionsRef = useRef<IsAutosaveBlockedOptions | undefined>(
    undefined,
  );
  const wasFirstAutoSave = useRef<boolean>(false);
  const isSavingRef = useRef<boolean>(isSaving);
  const isAutosaveBlockedRef = useRef(isAutosaveBlocked);
  // `watchData` (useWatch snapshot) is used only to detect changes and reschedule
  // the timer; the actual save payload is built from getValues() so it always
  // reflects the complete, current form state (matching manual save). Using the
  // useWatch snapshot as the payload could persist a partial/blank entry.
  const getValuesRef = useRef(getValues);
  const editingRef = useRef(editing);
  const activeAutosaveRef = useRef(activeAutosave);
  const pendingRef = useRef(false);
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  useEffect(() => {
    wasFirstAutoSave.current = false;
    // Skip autosave while the form is (re)populated programmatically on
    // mount / entry switch; the first real user edit will re-enable it.
    initialRender.current = true;
    const settle = setTimeout(() => {
      initialRender.current = false;
    }, 0);
    return () => clearTimeout(settle);
  }, [logbookId, idType, lbeidOrPartId, dispatch]);

  /**
   * Retrieves the ID object based on the provided ID type.
   *
   * @param entryId - An optional string representing the ID to retrieve.
   * @returns An object containing the ID type and its value.
   */
  const getId = useCallback(
    (entryId?: string) => ({ [String(idType)]: entryId ?? "new" }),
    [idType],
  );

  /**
   * Constructs the parameters for the auto-save API request.
   *
   * @param payload - The serialized logbook entry payload.
   * @returns An object containing the parameters for the API request.
   */
  const getParams = useCallback(
    (payload: string) => ({
      autosave: true,
      lbentry: payload,
      lbid: logbookId,
      yachtid: getYachtId(),
      _prename,
    }),
    [logbookId, _prename],
  );

  const buildPayload = useCallback(
    (data: Record<string, any>) =>
      convertLbEntryForSave(lbentry, data, false, false),
    [lbentry],
  );

  /**
   * Handles auto-saving for an opened logbook entry.
   *
   * @param data - An object containing the data to be saved.
   * @throws An error if the auto-save operation fails.
   */
  const autoSaveOpenedEntry = useCallback(
    async (data: Record<string, any>) => {
      const startTime = Date.now();
      crumb("AutoSave: start", {
        operation: "start",
        isNewEntry: false,
        entryId: lbeidOrPartId ?? id,
        logbookId,
      });

      dispatch(setAutoSaving({ isSaving: true }));
      const timeoutPromise = new Promise(resolve => setTimeout(resolve, 1000));
      const { payload, removedBlankRows } = buildPayload(data);
      const savePromise = post({
        request: "auto-save-entry",
        body: {
          params: {
            _current: current,
            entrydate,
            ...getId(lbeidOrPartId ?? id),
            ...getParams(payload),
            ...(removedBlankRows
              ? { reason: ESaveEntryReason.removedBlankRows }
              : {}),
          },
        },
      });

      try {
        const res = await Promise.all([timeoutPromise, savePromise]);
        if (res[1]) {
          const duration = Date.now() - startTime;
          crumb("AutoSave: success", {
            operation: "success",
            isNewEntry: false,
            entryId: lbeidOrPartId ?? id,
            logbookId,
            duration,
            status: res[1].status,
            autosave: res[1].autosave,
            hourlyRowsCount: sections[1]?.rows?.length ?? undefined,
          });

          dispatch(
            setLatestEntryStatus({
              status: res[1].status,
              autosave: res[1].autosave,
            }),
          );
          dispatch(setAutoSaving({ isSaving: false }));
          dispatch(setReleaseEntryLock(Math.random()));
          dispatch(setEditing(false));

          dispatch(setCurrent(res[1].current || getCurrentUTC()));

          void flushStoredChecklistQueue(
            post,
            lbeidOrPartId ?? id,
            dispatch,
          );

          await saveContactNumber();
        } else {
          crumb("AutoSave: error", {
            operation: "error",
            isNewEntry: false,
            entryId: lbeidOrPartId ?? id,
            logbookId,
            duration: Date.now() - startTime,
            error: "No response received from server",
          });

          dispatch(setAutoSaving({ isSaving: false, isError: true }));
          throw new Error("Auto save failed, please save the entry manually");
        }
      } catch (error) {
        crumb("AutoSave: error", {
          operation: "error",
          isNewEntry: false,
          entryId: lbeidOrPartId ?? id,
          logbookId,
          duration: Date.now() - startTime,
          error: error instanceof Error ? error.message : String(error),
        });

        dispatch(setAutoSaving({ isSaving: false, isError: true }));
        throw error;
      }
    },
    [
      buildPayload,
      current,
      dispatch,
      entrydate,
      getId,
      getParams,
      id,
      lbeidOrPartId,
      logbookId,
      post,
      saveContactNumber,
      sections,
      _prename,
    ],
  );

  /**
   * Handles auto-saving for a new logbook entry.
   *
   * @param data - An object containing the data to be saved.
   * @returns A string representing the ID of the newly saved entry.
   * @throws An error if the auto-save operation fails.
   */
  const autoSaveNewEntry = useCallback(
    async (data: Record<string, any>) => {
      const startTime = Date.now();
      crumb("AutoSave: start", {
        operation: "start",
        isNewEntry: true,
        logbookId,
      });

      dispatch(setAutoSaving({ isSaving: true }));
      const timeoutPromise = new Promise(resolve => setTimeout(resolve, 1000));
      const { payload, removedBlankRows } = buildPayload(data);
      const savePromise = post({
        request: "draft-save-entry",
        body: {
          params: {
            entrydate,
            ...getId(),
            ...getParams(payload),
            ...(removedBlankRows
              ? { reason: ESaveEntryReason.removedBlankRows }
              : {}),
          },
        },
        onSuccess: res => {
          let entryId = res[String(idType)] ?? "";
          if (res) {
            dispatch(setSendDataOnNewEntry(true));
            entryId = res.lbeid
              ? res.lbeid
              : res.partid
                ? res.partid
                : res.meid
                  ? res.meid
                  : null;
            dispatch(
              reassignNewChecklistActions({
                realId: entryId,
                lbid: logbookId,
                yachtid: getYachtId(),
              }),
            );
            void flushStoredChecklistQueue(post, entryId, dispatch);
          }

          if (!entryId) return;
          updateEntryRouteSilently(
            dispatch,
            navigate,
            setSearchParams,
            String(logbookId),
            String(idType),
            entryId,
          );
        },
      });

      try {
        const res = await Promise.all([timeoutPromise, savePromise]);

        if (res[1]) {
          const duration = Date.now() - startTime;
          const savedId = res[1].lbeid || res[1].partid || res[1].meid || null;

          crumb("AutoSave: success", {
            operation: "success",
            isNewEntry: true,
            entryId: savedId,
            logbookId,
            duration,
            savedId,
            response: res[1],
            hourlyRowsCount: sections[1]?.rows?.length ?? undefined,
          });

          dispatch(setAutoSaving({ isSaving: false }));
          dispatch(setReleaseEntryLock(Math.random()));
          dispatch(setEditing(false));
          dispatch(setTriggerInlineSave(persistInlineSave));
          setTimeout(() => {
            dispatch(setPersistInlineSave(-1));
          }, 1000);

          await saveContactNumber();

          return savedId;
        } else {
          crumb("AutoSave: error", {
            operation: "error",
            isNewEntry: true,
            logbookId,
            duration: Date.now() - startTime,
            error: "No response received from server",
          });

          dispatch(setAutoSaving({ isSaving: false, isError: true }));
          throw new Error("error on autosave in new entry");
        }
      } catch (error) {
        crumb("AutoSave: error", {
          operation: "error",
          isNewEntry: true,
          logbookId,
          duration: Date.now() - startTime,
          error: error instanceof Error ? error.message : String(error),
        });

        dispatch(setAutoSaving({ isSaving: false, isError: true }));
        throw error;
      }
    },
    [
      buildPayload,
      dispatch,
      entrydate,
      getId,
      getParams,
      idType,
      logbookId,
      navigate,
      persistInlineSave,
      post,
      saveContactNumber,
      sections,
      setSearchParams,
      _prename,
    ],
  );

  /**
   * Handles the save operation for both new and opened entries.
   *
   * @param data - An object containing the data to be saved.
   */
  const handleSave = useCallback(
    async (data: Record<string, any>) => {
      if (isReadOnly) return;
      if (isNewEntry || id === "new") {
        await autoSaveNewEntry(data);
      } else {
        await autoSaveOpenedEntry(data);
      }
      if (!wasFirstAutoSave.current) {
        dispatch(setShowAutoSaveModal(true));
        wasFirstAutoSave.current = true;
      }
    },
    [
      id,
      isNewEntry,
      autoSaveNewEntry,
      autoSaveOpenedEntry,
      dispatch,
      isReadOnly,
    ],
  );

  const handleSaveRef = useRef(handleSave);

  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  useEffect(() => {
    isAutosaveBlockedRef.current = isAutosaveBlocked;
  }, [isAutosaveBlocked]);

  useEffect(() => {
    getValuesRef.current = getValues;
  }, [getValues]);

  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  useEffect(() => {
    activeAutosaveRef.current = activeAutosave;
  }, [activeAutosave]);

  // Checks the block with `options` re-read fresh from the store rather than
  // via the subscribed ref, since an override (e.g. ignoreChecklistModalBlock)
  // only applies to this one triggered save, not to the hook's normal
  // reactive blocked-state tracking.
  const isBlockedNow = useCallback((options?: IsAutosaveBlockedOptions) => {
    if (!options) return isAutosaveBlockedRef.current;
    return selectIsAutosaveBlocked(store.getState(), options);
  }, []);

  const startAutosaveTimer = useCallback(
    (options?: IsAutosaveBlockedOptions) => {
      if (timer.current) clearTimeout(timer.current);
      if (isReadOnly) return;
      if (!editingRef.current) return;
      if (isSavingRef.current) return;
      if (!activeAutosaveRef.current || isBlockedNow(options)) return;

      scheduledOptionsRef.current = options;
      pendingRef.current = true;
      timer.current = setTimeout(async () => {
        if (!isOnline()) return;
        if (isBlockedNow(options)) return;
        pendingRef.current = false;
        scheduledOptionsRef.current = undefined;
        await handleSaveRef.current(getValuesRef.current());
      }, 3000);
    },
    [isReadOnly, isBlockedNow],
  );

  useEffect(() => {
    return registerAutosaveScheduleHandler(options => {
      if (initialRender.current) return;
      startAutosaveTimer(options);
    });
  }, [startAutosaveTimer]);

  /**
   * Immediately runs a pending autosave (skipping the debounce) so unsaved
   * changes are not lost when the user navigates away or backgrounds the tab.
   */
  const flushPendingAutosave = useCallback(() => {
    if (!pendingRef.current) return;
    if (isReadOnly) return;
    if (!editingRef.current || isSavingRef.current) return;
    if (!activeAutosaveRef.current || isAutosaveBlockedRef.current) return;
    if (!isOnline()) return;

    if (timer.current) clearTimeout(timer.current);
    pendingRef.current = false;
    void handleSaveRef.current(getValuesRef.current());
  }, [isReadOnly]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushPendingAutosave();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", flushPendingAutosave);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", flushPendingAutosave);
      // Flush on unmount (SPA navigation away from the entry) before the
      // entry lock is released elsewhere on teardown.
      flushPendingAutosave();
    };
  }, [flushPendingAutosave]);

  useEffect(() => {
    if (isReadOnly) return;
    if (!editing) return;
    if (isSavingRef.current) return;
    if (initialRender.current) return;

    if (timer.current && scheduledOptionsRef.current?.ignoreChecklistModalBlock) {
      return;
    }

    if (!activeAutosave || isAutosaveBlocked) {
      if (timer.current) clearTimeout(timer.current);
      return;
    }

    startAutosaveTimer();

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [
    _prename,
    activeAutosave,
    entrydate,
    id,
    isAutosaveBlocked,
    watchData,
    lbeidOrPartId,
    idType,
    logbookId,
    editing,
    isReadOnly,
    startAutosaveTimer,
  ]);
};
